'use client';

import { type ReactNode, useId, useState } from 'react';

import Image from 'next/image';

import { Check, ChevronDown, Package, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  BundleOfferItem,
  BundleProductRow,
} from '@kit/bookings/utils/addon-bundles';
import { composeSizedName } from '@kit/bookings/utils/addon-size-groups';
import { publicStorageUrl } from '@kit/shared/storage';
import { badgeVariants } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

export interface AddonBundleSelection {
  kitId: string;
  /** One product id per item, in item order: the chosen size or the item. */
  itemAddonIds: string[];
  /** Item names (with the chosen size), in item order, for summaries. */
  itemLabels: string[];
}

/** The size's own add-on id is what the kit line is sold as. */
function sizeLabel(size: BundleProductRow) {
  return size.variant_label ?? size.name;
}

type KitInfo = {
  id: string;
  name: string;
  price: number;
  description?: string | null;
  /** Storage path of the kit's first image. */
  imageUrl?: string | null;
};
type KitItems = BundleOfferItem<BundleProductRow>[];

interface ChosenItem {
  id: string;
  label: string;
  price: number;
}

/** "a, b and c" in the reader's language. */
function useListFormat() {
  const { i18n } = useTranslation('addons');
  const format = new Intl.ListFormat(i18n?.language, { type: 'conjunction' });

  return (names: string[]) => format.format(names);
}

function includedNames(items: KitItems) {
  return items.flatMap((item) =>
    item.kind === 'single' ? [item.product.name] : [],
  );
}

/** "Studs: 10 UK · Shorts: M" for an added kit's sized items. */
function describeSizes(items: KitItems, itemAddonIds: string[]) {
  const chosen = new Set(itemAddonIds);

  return items
    .flatMap((item) => {
      if (item.kind === 'single') return [];
      const size = item.sizes.find((candidate) => chosen.has(candidate.id));
      return size ? [`${item.baseName}: ${sizeLabel(size)}`] : [];
    })
    .join(' · ');
}

// The given sizes, else the only size of an item: nothing to choose there.
function initialChoices(
  items: KitItems,
  itemAddonIds: string[] = [],
): Record<string, string> {
  const given = new Set(itemAddonIds);

  return Object.fromEntries(
    items.flatMap((item) => {
      if (item.kind === 'single') return [];
      const size =
        item.sizes.find((candidate) => given.has(candidate.id)) ??
        (item.sizes.length === 1 ? item.sizes[0] : undefined);
      return size ? [[item.key, size.id]] : [];
    }),
  );
}

interface KitItemsChooserProps {
  kit: KitInfo;
  items: KitItems;
  onSubmit: (selection: AddonBundleSelection) => void;
  /** Sizes to start from, when changing an added kit. */
  initialItemAddonIds?: string[];
  submitLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * The kit's size choices: one line per sized item, the items without sizes
 * listed as included. The button stays disabled until every sized item has a
 * size; submitting resets the choices so the same kit can be added again in
 * other sizes.
 */
export function KitItemsChooser({
  kit,
  items,
  onSubmit,
  initialItemAddonIds,
  submitLabel,
  disabled,
  className,
}: KitItemsChooserProps) {
  const { t } = useTranslation('addons');
  const listFormat = useListFormat();
  const idPrefix = useId();
  const [chosen, setChosen] = useState(() =>
    initialChoices(items, initialItemAddonIds),
  );

  const chosenItems = items.map((item): ChosenItem | undefined => {
    if (item.kind === 'single') {
      const { id, name, price } = item.product;
      return { id, label: name, price: Number(price) };
    }

    const size = item.sizes.find(
      (candidate) => candidate.id === chosen[item.key],
    );

    return size
      ? {
          id: size.id,
          label: composeSizedName(item.baseName, sizeLabel(size)),
          price: Number(size.price),
        }
      : undefined;
  });
  const complete = chosenItems.every(Boolean);
  const listTotal = chosenItems.reduce(
    (sum, item) => sum + (item?.price ?? 0),
    0,
  );
  const saving = complete ? Math.max(0, listTotal - kit.price) : 0;
  const included = includedNames(items);

  const handleSubmit = () => {
    const picked = chosenItems.filter((item): item is ChosenItem =>
      Boolean(item),
    );
    if (picked.length !== items.length) return;

    onSubmit({
      kitId: kit.id,
      itemAddonIds: picked.map((item) => item.id),
      itemLabels: picked.map((item) => item.label),
    });
    setChosen(initialChoices(items));
  };

  return (
    <div className={cn('space-y-3', className)}>
      {kit.description ? (
        <p className="text-muted-foreground text-xs">{kit.description}</p>
      ) : null}

      {items.map((item) => {
        if (item.kind === 'single') return null;
        const labelId = `${idPrefix}-${item.key}`;

        return (
          <div key={item.key} className="flex items-start gap-3">
            <span
              id={labelId}
              title={item.baseName}
              className="text-muted-foreground w-24 shrink-0 truncate pt-2 text-xs font-medium"
            >
              {item.baseName}
            </span>
            <div
              role="group"
              aria-labelledby={labelId}
              className="flex flex-wrap gap-1.5"
            >
              {item.sizes.map((size) => {
                const selected = chosen[item.key] === size.id;
                const label = sizeLabel(size);

                return (
                  <Button
                    key={size.id}
                    type="button"
                    aria-pressed={selected}
                    variant={selected ? 'selected' : 'outline'}
                    size="sm"
                    className="min-h-11 min-w-11 px-3 text-xs"
                    disabled={disabled}
                    onClick={() =>
                      setChosen((current) => ({
                        ...current,
                        [item.key]: size.id,
                      }))
                    }
                    data-test={'addon-bundle-size-' + label}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      })}

      {included.length > 0 ? (
        <p
          className="text-muted-foreground flex items-start gap-1.5 text-xs"
          data-test="addon-bundle-included-item"
        >
          <Check className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          {t('bundles.includedItems', { items: listFormat(included) })}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            'inline-flex items-baseline gap-1 text-xs',
            complete ? 'text-success' : 'text-muted-foreground',
          )}
          aria-live="polite"
        >
          {complete ? (
            saving > 0 ? (
              <>
                {t('bundles.saving')}
                <span className="inline-flex items-baseline gap-0.5">
                  <CurrencyIcon className="h-3 w-3 self-center" />
                  {saving.toFixed(2)}
                </span>
              </>
            ) : null
          ) : (
            t('bundles.chooseEverySize')
          )}
        </span>
        <Button
          type="button"
          size="sm"
          className="h-8"
          onClick={handleSubmit}
          disabled={disabled || !complete}
          data-test="addon-bundle-add"
        >
          {submitLabel ?? t('bundles.addKit')}
        </Button>
      </div>
    </div>
  );
}

/** A kit's photo, or nothing when it has none. */
function KitThumbnail({ path }: { path?: string | null }) {
  if (!path) return null;

  return (
    <Image
      src={publicStorageUrl(path)}
      alt=""
      width={40}
      height={40}
      className="h-10 w-10 shrink-0 rounded-md border object-cover"
    />
  );
}

export interface KitRowSelection {
  key: string;
  itemAddonIds: string[];
  itemLabels: string[];
  /** After Edit: lock, remove or quantity. */
  actions?: ReactNode;
  /** False for a paid or shared kit, which is refunded rather than changed. */
  editable?: boolean;
}

type RowMode =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; key: string };

interface AddonBundleRowProps {
  kit: KitInfo;
  items: KitItems;
  /** Right-hand side of the row, before the chevron. */
  price: ReactNode;
  /** The kits already added from this row. */
  selections: KitRowSelection[];
  onAdd: (selection: AddonBundleSelection) => void;
  /** Changes an added kit's sizes; Edit is hidden without it. */
  onReplace?: (key: string, selection: AddonBundleSelection) => void;
  disabled?: boolean;
  /** A selection mark at the start, as the surface's other add-on rows have. */
  leading?: ReactNode;
  /** Wraps the clickable row, e.g. in a menu item; a button by default. */
  renderToggle?: (toggle: {
    content: ReactNode;
    open: boolean;
    panelId: string;
    onToggle: () => void;
  }) => ReactNode;
}

/**
 * A kit as one add-on row. Tapping it opens the size choices underneath;
 * adding closes them. Added kits are listed in the row with their sizes and
 * an Edit that reopens the choices on those sizes.
 */
export function AddonBundleRow({
  kit,
  items,
  price,
  selections,
  onAdd,
  onReplace,
  disabled,
  leading,
  renderToggle,
}: AddonBundleRowProps) {
  const { t } = useTranslation('addons');
  const listFormat = useListFormat();
  const panelId = useId();
  const [mode, setMode] = useState<RowMode>({ kind: 'closed' });

  const open = mode.kind !== 'closed';
  const added = selections.length > 0;
  const editing =
    mode.kind === 'edit'
      ? selections.find((selection) => selection.key === mode.key)
      : undefined;
  const included = includedNames(items);
  const onToggle = () => setMode(open ? { kind: 'closed' } : { kind: 'add' });

  const handleSubmit = (selection: AddonBundleSelection) => {
    if (editing) {
      const unchanged =
        editing.itemAddonIds.join() === selection.itemAddonIds.join();
      if (!unchanged) onReplace?.(editing.key, selection);
    } else {
      onAdd(selection);
    }
    setMode({ kind: 'closed' });
  };

  // Lines under the row start where the kit's name does.
  const indent = (
    <>
      {leading ? <span className="w-3 shrink-0" /> : null}
      {kit.imageUrl ? <span className="w-10 shrink-0" /> : null}
    </>
  );

  const content = (
    <span className="flex w-full items-start gap-2.5 text-start">
      {leading ? (
        <span className="flex w-3 shrink-0 justify-center pt-1">{leading}</span>
      ) : null}
      <KitThumbnail path={kit.imageUrl} />
      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{kit.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {t('bundles.badge')}
          </span>
        </span>
        {added ? null : (
          <span className="text-muted-foreground line-clamp-2 text-xs">
            {listFormat(
              items.map((item) =>
                item.kind === 'sized' ? item.baseName : item.product.name,
              ),
            )}
          </span>
        )}
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        {added ? (
          <span
            className={cn(
              badgeVariants({ variant: 'success' }),
              'gap-1 px-1.5 py-0 text-[10px]',
            )}
          >
            <Check className="h-3 w-3" aria-hidden />
            {t('bundles.addedCount', { count: selections.length })}
          </span>
        ) : null}
        <span className="flex items-center gap-1">
          {price}
          <ChevronDown
            className={cn(
              'h-4 w-4 opacity-50 transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </span>
      </span>
    </span>
  );

  return (
    <div
      className={cn(
        'rounded-md border',
        added ? 'border-primary/30 bg-primary/5' : 'border-transparent',
      )}
      data-test="addon-bundle-row"
    >
      {renderToggle ? (
        renderToggle({ content, open, panelId, onToggle })
      ) : (
        <button
          type="button"
          className="w-full rounded-md p-2"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          data-test="addon-bundle-toggle"
        >
          {content}
        </button>
      )}

      {added ? (
        <div className="flex gap-2.5 px-2 pb-2">
          {indent}
          <div className="min-w-0 flex-1 space-y-1">
            {selections.map((selection) => (
              <div
                key={selection.key}
                className="flex items-center justify-between gap-2"
                data-test="kit-selection-line"
              >
                <p className="min-w-0 text-xs break-words">
                  {describeSizes(items, selection.itemAddonIds) ||
                    selection.itemLabels.join(', ')}
                </p>
                <div className="flex shrink-0 items-center gap-1">
                  {onReplace && selection.editable !== false ? (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-7 gap-1 px-1 text-xs"
                      disabled={disabled}
                      aria-label={t('bundles.editKit', { name: kit.name })}
                      onClick={() =>
                        setMode({ kind: 'edit', key: selection.key })
                      }
                      data-test="addon-bundle-edit"
                    >
                      <Pencil className="h-3 w-3" aria-hidden />
                      {t('bundles.edit')}
                    </Button>
                  ) : null}
                  {selection.actions}
                </div>
              </div>
            ))}
            {/* The open choices list the included items themselves. */}
            {included.length > 0 && !open ? (
              <p className="text-muted-foreground text-xs">
                {t('bundles.includedItems', { items: listFormat(included) })}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div id={panelId}>
        {open ? (
          <KitItemsChooser
            key={editing?.key ?? 'add'}
            kit={kit}
            items={items}
            disabled={disabled}
            initialItemAddonIds={editing?.itemAddonIds}
            submitLabel={editing ? t('bundles.saveKit') : undefined}
            className="mx-2 border-t pt-3 pb-2"
            onSubmit={handleSubmit}
          />
        ) : null}
      </div>
    </div>
  );
}

interface AddonBundleCardProps {
  kit: KitInfo;
  /** The kit's items as buildBundleOffers returns them. */
  items: KitItems;
  onAdd: (selection: AddonBundleSelection) => void;
  disabled?: boolean;
  /** Layout only, e.g. dropping the border where a popover already frames it. */
  className?: string;
}

/** A kit with its size choices, for a surface that opens one kit at a time (the POS popover). */
export function AddonBundleCard({
  kit,
  items,
  onAdd,
  disabled,
  className,
}: AddonBundleCardProps) {
  const { t } = useTranslation('addons');

  return (
    <div
      className={cn('space-y-3 rounded-md border p-3', className)}
      data-test="addon-bundle-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Package
            className="text-muted-foreground h-4 w-4 shrink-0"
            aria-hidden
          />
          <span className="truncate text-sm font-medium">{kit.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {t('bundles.badge')}
          </span>
        </div>
        <span className="inline-flex shrink-0 items-baseline gap-0.5 text-sm font-semibold">
          <CurrencyIcon className="h-3 w-3 self-center" />
          {kit.price.toFixed(2)}
        </span>
      </div>

      <KitItemsChooser
        kit={kit}
        items={items}
        onSubmit={onAdd}
        disabled={disabled}
      />
    </div>
  );
}
